import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import type { Session } from '@supabase/supabase-js';
import { config } from '../lib/config';
import {
  PLAY_MODE_COPY_DEFAULTS,
  PLAY_MODE_COPY_FIELDS,
  PLAY_MODE_COPY_PREVIEW_SAMPLE_VARS,
  PLAY_MODE_COPY_SPORT_EDITOR_OPTIONS,
  PLAY_MODE_COPY_STYLE_EDITOR_OPTIONS,
  PLAY_MODE_COPY_VARIANT_EDITOR_OPTIONS,
  cloneValue,
  countOverridesForFields,
  deletePathValue,
  fieldsForSection,
  getContextualPathValue,
  getEffectivePlayModePickerCopy,
  getPathValue,
  normalizePlayModePickerOverrides,
  resolveScopedFieldPath,
  sectionLabel,
  setPathValue,
  textValue,
  type PlayModeCopyEditorScope,
  type PlayModeCopyField,
  type PlayModeCopyPreviewContext,
} from './schema';

type StatusTone = 'info' | 'success' | 'error' | 'warning';
type SaveState = 'saved' | 'saving' | 'unsaved' | 'error';
type PreviewViewport = 'phone' | 'tablet' | 'desktop';
type PreviewTarget = 'base' | 'style' | 'sport' | 'variant';

type StatusMessage = {
  tone: StatusTone;
  text: string;
};

type AdminConsoleResponse<T> = { ok?: boolean; error?: string; value?: unknown; settings?: unknown } & T;

type DraftMeta = {
  updated_at: string | null;
  updated_by: string | null;
};

type PlayModeCopyGetResponse = {
  value?: Record<string, unknown>;
  settings?: { updated_at?: string | null; updated_by?: string | null } | null;
};

type PreviewMessage = {
  source: 'rvlry-admin-play-mode-copy';
  type: 'play-mode-copy-preview:update';
  payload?: {
    copySettings?: Record<string, unknown> | null;
    sportId?: string | null;
    variant?: string | null;
    viewport?: string | null;
  };
};

const PLAY_MODE_COPY_KEY = 'play_mode_picker_copy_v1';
const DEFAULT_EDITOR_SCOPE: PlayModeCopyEditorScope = { kind: 'base' };
const DEFAULT_PREVIEW_CONTEXT: PlayModeCopyPreviewContext = {
  copyStyle: 'game',
  sportId: 'soccer',
  variant: null,
  activityLabel: 'Soccer',
};

function scopeSummary(scope: PlayModeCopyEditorScope) {
  switch (scope.kind) {
    case 'style':
      return `Family override · ${scope.copyStyle}`;
    case 'sport':
      return `Sport override · ${scope.sportId}`;
    case 'variant':
      return `Variant override · ${scope.sportId} / ${scope.variant}`;
    case 'base':
    default:
      return 'Base copy';
  }
}

async function adminFetch<T>(
  session: Session,
  payload: Record<string, unknown>
): Promise<AdminConsoleResponse<T>> {
  const url = `${config.supabaseUrl}/functions/v1/admin-console`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      apikey: config.supabaseAnonKey,
      authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify(payload),
  });
  const body = (await response.json().catch(() => ({}))) as AdminConsoleResponse<T>;
  if (!response.ok || body?.error) {
    throw new Error(body?.error || `Request failed (${response.status})`);
  }
  return body;
}

function sectionDescription(section: PlayModeCopyField['section']) {
  switch (section) {
    case 'sheet':
      return 'Sheet title and subtitle framing.';
    case 'quick_play':
      return 'Head-to-head mode language.';
    case 'individuals':
      return 'Free-for-all mode language for individual winners.';
    case 'teams':
      return 'Fixed-side mode language. Hidden unless MODE_TEAMS_V1 is enabled.';
    case 'score_challenge':
      return 'Solo mode language.';
    case 'tournament':
      return 'Bracket mode language.';
    default:
      return '';
  }
}

export default function PlayModeCopyWorkspace({ session }: { session: Session }) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [status, setStatus] = useState<StatusMessage | null>(null);
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState<SaveState>('saved');
  const [meta, setMeta] = useState<DraftMeta>({ updated_at: null, updated_by: null });
  const [baseline, setBaseline] = useState<Record<string, unknown>>({});
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [editorScope, setEditorScope] = useState<PlayModeCopyEditorScope>(DEFAULT_EDITOR_SCOPE);
  const [selectedFieldPath, setSelectedFieldPath] = useState<string>(PLAY_MODE_COPY_FIELDS[0]?.path ?? '');
  const [previewViewport, setPreviewViewport] = useState<PreviewViewport>('phone');
  const [previewTarget, setPreviewTarget] = useState<PreviewTarget>('sport');
  const [previewContext, setPreviewContext] = useState<PlayModeCopyPreviewContext>(DEFAULT_PREVIEW_CONTEXT);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [iframeError, setIframeError] = useState<string | null>(null);

  const previewOrigin = useMemo(() => {
    if (typeof window === 'undefined') return 'https://play.rvlryapp.com';
    const params = new URLSearchParams(window.location.search);
    const overridden = params.get('playPreviewOrigin');
    if (overridden) return overridden.replace(/\/$/, '');
    return 'https://play.rvlryapp.com';
  }, []);

  const previewUrl = useMemo(() => {
    const query = new URLSearchParams({
      sportId: previewContext.sportId ?? 'soccer',
      variant: previewContext.variant ?? '',
      viewport: previewViewport,
    });
    return `${previewOrigin}/preview/play-mode-picker?${query.toString()}`;
  }, [previewContext.sportId, previewContext.variant, previewOrigin, previewViewport]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const result = await adminFetch<PlayModeCopyGetResponse>(session, { action: 'play_mode_copy:get' });
        if (cancelled) return;
        const value =
          result.value && typeof result.value === 'object' && !Array.isArray(result.value)
            ? (result.value as Record<string, unknown>)
            : {};
        setBaseline(value);
        setDraft(cloneValue(value));
        setMeta({
          updated_at: result.settings?.updated_at ?? null,
          updated_by: result.settings?.updated_by ?? null,
        });
        setStatus(null);
        setSaveState('saved');
      } catch (error) {
        if (cancelled) return;
        setStatus({ tone: 'error', text: (error as Error).message || 'Failed to load play mode copy.' });
        setSaveState('error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [session]);

  const isDirty = useMemo(
    () => JSON.stringify(normalizePlayModePickerOverrides(draft)) !== JSON.stringify(normalizePlayModePickerOverrides(baseline)),
    [baseline, draft]
  );

  useEffect(() => {
    setSaveState((current) => {
      if (current === 'saving') return current;
      return isDirty ? 'unsaved' : 'saved';
    });
  }, [isDirty]);

  const effectivePreview = useMemo(
    () =>
      getEffectivePlayModePickerCopy(
        draft,
        previewTarget === 'base'
          ? null
          : previewTarget === 'style'
            ? { copyStyle: previewContext.copyStyle }
            : previewTarget === 'sport'
              ? { copyStyle: previewContext.copyStyle, sportId: previewContext.sportId }
              : previewContext
      ),
    [draft, previewContext, previewTarget]
  );

  const selectedField = useMemo(
    () => PLAY_MODE_COPY_FIELDS.find((field) => field.path === selectedFieldPath) ?? PLAY_MODE_COPY_FIELDS[0] ?? null,
    [selectedFieldPath]
  );

  const selectedScopedPath = useMemo(
    () => (selectedField ? resolveScopedFieldPath(selectedField.path, editorScope) : ''),
    [editorScope, selectedField]
  );

  const selectedValue = useMemo(
    () => textValue(getPathValue(draft, selectedScopedPath)),
    [draft, selectedScopedPath]
  );

  const selectedDefaultValue = useMemo(
    () =>
      selectedField
        ? textValue(
            getContextualPathValue(
              getEffectivePlayModePickerCopy({}, previewTarget === 'base' ? null : previewContext),
              selectedField.path,
              previewTarget === 'base' ? null : previewContext
            )
          )
        : '',
    [previewContext, previewTarget, selectedField]
  );

  const pushPreviewState = useCallback(() => {
    const target = iframeRef.current?.contentWindow;
    if (!target) return;
    target.postMessage(
      {
        source: 'rvlry-admin-play-mode-copy',
        type: 'play-mode-copy-preview:update',
        payload: {
          copySettings: effectivePreview,
          sportId: previewContext.sportId,
          variant: previewContext.variant,
          viewport: previewViewport,
        },
      } satisfies PreviewMessage,
      previewOrigin
    );
  }, [effectivePreview, previewContext.sportId, previewContext.variant, previewOrigin, previewViewport]);

  useEffect(() => {
    if (!iframeLoaded) return;
    const timer = window.setTimeout(() => pushPreviewState(), 80);
    return () => window.clearTimeout(timer);
  }, [iframeLoaded, pushPreviewState]);

  const updateField = useCallback(
    (field: PlayModeCopyField, nextValue: string) => {
      setDraft((current) => {
        const next = cloneValue(current);
        const scopedPath = resolveScopedFieldPath(field.path, editorScope);
        const defaultValue = textValue(getPathValue(PLAY_MODE_COPY_DEFAULTS, field.path));
        if (!nextValue.trim().length || (editorScope.kind === 'base' && nextValue === defaultValue)) {
          deletePathValue(next, scopedPath);
        } else {
          setPathValue(next, scopedPath, nextValue);
        }
        return next;
      });
    },
    [editorScope]
  );

  const saveDraft = useCallback(async () => {
    try {
      setSaveState('saving');
      setStatus({ tone: 'info', text: 'Saving play mode copy…' });
      const value = normalizePlayModePickerOverrides(draft);
      const result = await adminFetch<PlayModeCopyGetResponse>(session, {
        action: 'play_mode_copy:save',
        value,
        expectedUpdatedAt: meta.updated_at,
      });
      const nextValue =
        result.value && typeof result.value === 'object' && !Array.isArray(result.value)
          ? (result.value as Record<string, unknown>)
          : {};
      setBaseline(nextValue);
      setDraft(cloneValue(nextValue));
      setMeta({
        updated_at: result.settings?.updated_at ?? null,
        updated_by: result.settings?.updated_by ?? null,
      });
      setSaveState('saved');
      setStatus({ tone: 'success', text: 'Play mode copy saved.' });
    } catch (error) {
      setSaveState('error');
      setStatus({ tone: 'error', text: (error as Error).message || 'Failed to save play mode copy.' });
    }
  }, [draft, meta.updated_at, session]);

  const sectionGroups = useMemo(
    () =>
      (['sheet', 'quick_play', 'individuals', 'teams', 'score_challenge', 'tournament'] as const).map((section) => ({
        section,
        label: sectionLabel(section),
        description: sectionDescription(section),
        fields: fieldsForSection(section),
      })),
    []
  );

  const editorVariantOptions = useMemo(
    () => (editorScope.kind === 'variant' ? PLAY_MODE_COPY_VARIANT_EDITOR_OPTIONS[editorScope.sportId] ?? [] : []),
    [editorScope]
  );

  useEffect(() => {
    if (editorScope.kind === 'variant' && editorVariantOptions.length > 0) {
      if (!editorVariantOptions.some((option) => option.value === editorScope.variant)) {
        setEditorScope({ kind: 'variant', sportId: editorScope.sportId, variant: editorVariantOptions[0].value });
      }
    }
  }, [editorScope, editorVariantOptions]);

  useEffect(() => {
    if (!selectedField && PLAY_MODE_COPY_FIELDS[0]) setSelectedFieldPath(PLAY_MODE_COPY_FIELDS[0].path);
  }, [selectedField]);

  useEffect(() => {
    if (previewTarget === 'variant') {
      const options = PLAY_MODE_COPY_VARIANT_EDITOR_OPTIONS[previewContext.sportId ?? ''] ?? [];
      if (options.length === 0) {
        setPreviewTarget('sport');
        return;
      }
      if (!options.some((option) => option.value === previewContext.variant)) {
        setPreviewContext((current) => ({ ...current, variant: options[0].value }));
      }
    }
  }, [previewContext.sportId, previewContext.variant, previewTarget]);

  if (loading) {
    return <div className="admin-card">Loading play mode copy…</div>;
  }

  return (
    <div className="cms-shell">
      <div className="admin-card" style={{ gap: 10 }}>
        <div className="admin-section-title">Play mode copy</div>
        <div className="admin-muted">
          Activity-aware copy for the create-flow format picker. Stored in{' '}
          <span className="admin-mono">app_settings.{PLAY_MODE_COPY_KEY}</span>.
        </div>
        <div className="admin-muted">
          Template vars: <span className="admin-mono">{'{activityLabel}'}</span>,{' '}
          <span className="admin-mono">{'{activityQuestionPhrase}'}</span>,{' '}
          <span className="admin-mono">{'{activityPlanPhrase}'}</span>,{' '}
          <span className="admin-mono">{'{activityPrimaryCta}'}</span>,{' '}
          <span className="admin-mono">{'{copyStyle}'}</span>,{' '}
          <span className="admin-mono">{'{sportId}'}</span>,{' '}
          <span className="admin-mono">{'{variant}'}</span>.
        </div>
        <div className="admin-muted">
          Sample vars: <span className="admin-mono">{JSON.stringify(PLAY_MODE_COPY_PREVIEW_SAMPLE_VARS)}</span>
        </div>
        <div className="admin-row-wrap">
          <div className="admin-field">
            <label>Scope</label>
            <select
              className="admin-input"
              value={editorScope.kind}
              onChange={(event) => {
                const next = event.target.value;
                if (next === 'style') {
                  setEditorScope({ kind: 'style', copyStyle: PLAY_MODE_COPY_STYLE_EDITOR_OPTIONS[0].value });
                  return;
                }
                if (next === 'sport') {
                  setEditorScope({ kind: 'sport', sportId: PLAY_MODE_COPY_SPORT_EDITOR_OPTIONS[0].value });
                  return;
                }
                if (next === 'variant') {
                  const sportId = Object.keys(PLAY_MODE_COPY_VARIANT_EDITOR_OPTIONS)[0] ?? PLAY_MODE_COPY_SPORT_EDITOR_OPTIONS[0].value;
                  const variant = PLAY_MODE_COPY_VARIANT_EDITOR_OPTIONS[sportId]?.[0]?.value ?? '';
                  if (variant) setEditorScope({ kind: 'variant', sportId, variant });
                  else setEditorScope({ kind: 'sport', sportId });
                  return;
                }
                setEditorScope({ kind: 'base' });
              }}
            >
              <option value="base">Base</option>
              <option value="style">Family</option>
              <option value="sport">Sport</option>
              <option value="variant">Variant</option>
            </select>
          </div>
          {editorScope.kind === 'style' ? (
            <div className="admin-field">
              <label>Family</label>
              <select
                className="admin-input"
                value={editorScope.copyStyle}
                onChange={(event) => setEditorScope({ kind: 'style', copyStyle: event.target.value as any })}
              >
                {PLAY_MODE_COPY_STYLE_EDITOR_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          {editorScope.kind === 'sport' || editorScope.kind === 'variant' ? (
            <div className="admin-field">
              <label>Sport</label>
              <select
                className="admin-input"
                value={editorScope.sportId}
                onChange={(event) => {
                  const sportId = event.target.value;
                  if (editorScope.kind === 'variant') {
                    const variant = PLAY_MODE_COPY_VARIANT_EDITOR_OPTIONS[sportId]?.[0]?.value ?? '';
                    if (variant) setEditorScope({ kind: 'variant', sportId, variant });
                    else setEditorScope({ kind: 'sport', sportId });
                    return;
                  }
                  setEditorScope({ kind: 'sport', sportId });
                }}
              >
                {PLAY_MODE_COPY_SPORT_EDITOR_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          {editorScope.kind === 'variant' ? (
            <div className="admin-field">
              <label>Variant</label>
              <select
                className="admin-input"
                value={editorScope.variant}
                onChange={(event) =>
                  setEditorScope({ kind: 'variant', sportId: editorScope.sportId, variant: event.target.value })
                }
              >
                {editorVariantOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          <div className="admin-field" style={{ minWidth: 220 }}>
            <label>Editing</label>
            <div className="admin-muted">{scopeSummary(editorScope)}</div>
          </div>
          <div className="admin-actions" style={{ marginLeft: 'auto' }}>
            <button className="admin-btn" type="button" onClick={() => setDraft(cloneValue(baseline))} disabled={!isDirty}>
              Reset
            </button>
            <button className="admin-btn primary" type="button" onClick={() => void saveDraft()} disabled={!isDirty || saveState === 'saving'}>
              {saveState === 'saving' ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
        {status ? <div className={clsx('admin-status', status.tone)}>{status.text}</div> : null}
      </div>

      <div className="admin-grid-2" style={{ alignItems: 'start' }}>
        <div className="admin-card" style={{ gap: 14 }}>
          {sectionGroups.map((group) => {
            const overrideCount = countOverridesForFields(draft, group.fields, editorScope);
            return (
              <div key={group.section} style={{ gap: 8, display: 'grid' }}>
                <div className="admin-row-wrap" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <div>
                    <div className="admin-section-title">{group.label}</div>
                    <div className="admin-muted">{group.description}</div>
                  </div>
                  <div className="admin-muted">{overrideCount} overrides</div>
                </div>
                <div style={{ display: 'grid', gap: 10 }}>
                  {group.fields.map((field) => {
                    const scopedPath = resolveScopedFieldPath(field.path, editorScope);
                    const value = textValue(getPathValue(draft, scopedPath));
                    const effectiveValue = textValue(
                      getContextualPathValue(
                        getEffectivePlayModePickerCopy(draft, previewTarget === 'base' ? null : previewContext),
                        field.path,
                        previewTarget === 'base' ? null : previewContext
                      )
                    );
                    const active = field.path === selectedFieldPath;
                    return (
                      <button
                        key={field.path}
                        type="button"
                        className={clsx('cms-field-row', active && 'active')}
                        onClick={() => setSelectedFieldPath(field.path)}
                      >
                        <span>
                          <strong>{field.label}</strong>
                          <span className="admin-muted" style={{ display: 'block' }}>
                            {field.path}
                          </span>
                        </span>
                        <span className="admin-muted" style={{ maxWidth: 240, textAlign: 'right' }}>
                          {(value || effectiveValue || '—').slice(0, 80)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ display: 'grid', gap: 16 }}>
          <div className="admin-card" style={{ gap: 10 }}>
            <div className="admin-section-title">{selectedField?.label ?? 'Field'}</div>
            <div className="admin-muted">{selectedField?.path}</div>
            <textarea
              className="admin-input"
              rows={6}
              value={selectedValue}
              onChange={(event) => selectedField && updateField(selectedField, event.target.value)}
              placeholder={selectedDefaultValue}
            />
            <div className="admin-muted">
              Default: <span className="admin-mono">{selectedDefaultValue || '—'}</span>
            </div>
          </div>

          <div className="admin-card" style={{ gap: 12 }}>
            <div className="admin-row-wrap" style={{ justifyContent: 'space-between' }}>
              <div>
                <div className="admin-section-title">Preview</div>
                <div className="admin-muted">Real create-flow sheet with contextual overrides applied.</div>
              </div>
              <div className="admin-row-wrap">
                <select
                  className="admin-input"
                  value={previewViewport}
                  onChange={(event) => setPreviewViewport(event.target.value as PreviewViewport)}
                >
                  <option value="phone">Phone</option>
                  <option value="tablet">Tablet</option>
                  <option value="desktop">Desktop</option>
                </select>
                <select
                  className="admin-input"
                  value={previewTarget}
                  onChange={(event) => setPreviewTarget(event.target.value as PreviewTarget)}
                >
                  <option value="base">Base</option>
                  <option value="style">Family</option>
                  <option value="sport">Sport</option>
                  <option value="variant">Variant</option>
                </select>
              </div>
            </div>
            <div className="admin-row-wrap">
              <div className="admin-field">
                <label>Family</label>
                <select
                  className="admin-input"
                  value={previewContext.copyStyle}
                  onChange={(event) =>
                    setPreviewContext((current) => ({ ...current, copyStyle: event.target.value as any }))
                  }
                >
                  {PLAY_MODE_COPY_STYLE_EDITOR_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="admin-field">
                <label>Sport</label>
                <select
                  className="admin-input"
                  value={previewContext.sportId ?? ''}
                  onChange={(event) =>
                    setPreviewContext((current) => ({ ...current, sportId: event.target.value, variant: null }))
                  }
                >
                  {PLAY_MODE_COPY_SPORT_EDITOR_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="admin-field">
                <label>Variant</label>
                <select
                  className="admin-input"
                  value={previewContext.variant ?? ''}
                  onChange={(event) =>
                    setPreviewContext((current) => ({ ...current, variant: event.target.value || null }))
                  }
                  disabled={(PLAY_MODE_COPY_VARIANT_EDITOR_OPTIONS[previewContext.sportId ?? ''] ?? []).length === 0}
                >
                  <option value="">None</option>
                  {(PLAY_MODE_COPY_VARIANT_EDITOR_OPTIONS[previewContext.sportId ?? ''] ?? []).map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="admin-preview-shell" style={{ minHeight: 760 }}>
              <iframe
                ref={iframeRef}
                title="Play mode copy preview"
                src={previewUrl}
                className="admin-preview-frame"
                onLoad={() => {
                  setIframeLoaded(true);
                  setIframeError(null);
                }}
                onError={() => setIframeError('Preview failed to load.')}
              />
            </div>
            {iframeError ? <div className="admin-status error">{iframeError}</div> : null}
          </div>
        </div>
      </div>

      <details className="admin-details">
        <summary>Advanced — Raw JSON</summary>
        <div className="admin-grid-2" style={{ marginTop: 12 }}>
          <div className="admin-card">
            <div className="admin-section-title">Overrides (saved)</div>
            <pre className="admin-code">{JSON.stringify(draft, null, 2)}</pre>
          </div>
          <div className="admin-card">
            <div className="admin-section-title">Effective (merged)</div>
            <pre className="admin-code">{JSON.stringify(effectivePreview, null, 2)}</pre>
          </div>
        </div>
        <div className="admin-muted" style={{ marginTop: 10 }}>
          Last updated {meta.updated_at ?? '—'}{meta.updated_by ? ` by ${String(meta.updated_by).slice(0, 8)}…` : ''}.
        </div>
      </details>
    </div>
  );
}
